import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Icon, Link, LoadingGroup, LoadingSkeleton, Panel, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Heading, Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";

import {
  Pharmacy,
  useCreateFavoritePharmacyMutation,
  useListFavoritePharmaciesQuery,
  useUpdateFavoritePharamcyMutation,
} from "@/App/api/pharmacy";
import { ThemedButton } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { capitalizeString, formatPhoneNumber, getCustomerSupportNumber } from "@/App/utils/utils";

import { AppleMap } from "capitalrx-components-2.0/lib/FindBestPriceTool";
import { FindBestPriceDetailSection, NearbyPharmacyDetailSection } from "./DetailSection";

const TRANSLATION_PATH = "PRICING_DETAIL_PANELS.pharmacyDetails";

interface PharmacyDetailsProps extends Partial<Pharmacy> {
  readonly useCustomerSupportHotline?: boolean;
  readonly isLoading: boolean;
  readonly context?: "find_best_price" | "nearby_pharmacies";
  readonly customerSupportNumber?: string;
}

export const NearbyPharmacyDetails: FC<PharmacyDetailsProps> = (props) => {
  const { t: translate } = useTranslation();
  const {
    provider_id: providerId,
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    city,
    state,
    zip,
    customerSupportNumber,
    isLoading,
    name: pharmacyName,
    hours: pharmacyHours,
    services: pharmacyServices,
    languages: pharmacyLanguages,
    minority_owned_pharmacy: isMinorityOwnedPharmacy,
  } = props;

  const alert = useAlert();
  const { isViewingDataholder } = useContext(MemberEligibilityContext);
  const {
    data: favoritePharmacies,
    isLoading: isLoadingFavoritePharmacies,
    isRefetching: isRefetchingFavoritePharmacies,
    refetch: refetchFavoritePharmacies,
  } = useListFavoritePharmaciesQuery();
  const { mutateAsync: updateFavoritePharmacy, isPending: isUpdatingFavoritePharmacy } = useUpdateFavoritePharamcyMutation();
  const { mutateAsync: createFavoritePharmacy, isPending: isCreatingFavoritePharmacy } = useCreateFavoritePharmacyMutation();
  const isEmulating = !!sessionStorage.getItem("isEmulation");

  const isFavoritePharmacy = useMemo(() => {
    return (favoritePharmacies?.results || []).map((pharm) => pharm.pharmacy.provider_id).includes(providerId);
  }, [favoritePharmacies, providerId]);

  const toggleFavoritePharmacy = useCallback(async () => {
    try {
      if (isFavoritePharmacy) {
        await updateFavoritePharmacy(providerId);
      } else {
        await createFavoritePharmacy(providerId);
      }
      await refetchFavoritePharmacies();
    } catch (e) {
      const errorMessage = e?.data?.message
      || translate(`${TRANSLATION_PATH}.${isFavoritePharmacy ? "remove" : "set"}FavoritePharmacy`);
      alert({
        message: errorMessage,
        type: "error",
        style: "toast",
      });
    }
  }, [isFavoritePharmacy, providerId]);

  const pharmacyAddress = useMemo(() => {
    if ((addressLine1 || addressLine2) && city && state && zip) {
      return `${capitalizeString(addressLine1)} ${addressLine2}, ${capitalizeString(city)}, ${state} ${zip}`;
    }
    return "";
  }, [addressLine1, addressLine2, city, state, zip]);

  const isFavoriteButtonDisabled = useMemo(() => {
    return (
      isViewingDataholder
      || isEmulating
      || isUpdatingFavoritePharmacy
      || isCreatingFavoritePharmacy
      || isLoadingFavoritePharmacies
      || isRefetchingFavoritePharmacies
    );
  }, [
    isViewingDataholder,
    isEmulating,
    isUpdatingFavoritePharmacy,
    isCreatingFavoritePharmacy,
    isLoadingFavoritePharmacies,
    isRefetchingFavoritePharmacies,
  ]);

  return (
    <div>
      <LoadingGroup loading={isLoading}>
        <Stack spacing="small">
          <Shelf>
            <Subheading>{translate(`${TRANSLATION_PATH}.pharmacyDetails`)}</Subheading>
            <Shelf.Spacer />
            {!isLoadingFavoritePharmacies && (
              <ThemedButton
                disabled={isFavoriteButtonDisabled}
                testId="favorite-pharmacy"
                appearance="text"
                onClick={toggleFavoritePharmacy}
                icon={isFavoritePharmacy ? "star-filled" : "star-empty"}
              >
                {isFavoritePharmacy && translate(`${TRANSLATION_PATH}.removeFavorite`)}
                {!isFavoritePharmacy && translate(`${TRANSLATION_PATH}.saveFavorite`)}
              </ThemedButton>
            )}
          </Shelf>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <NearbyPharmacyDetailSection alignment="top">
              <Bold>{translate(`${TRANSLATION_PATH}.name`)}: </Bold>
              <Stack spacing="none">
                <Paragraph testId="pharmacy-name">{pharmacyName}</Paragraph>
                {isMinorityOwnedPharmacy && (
                  <Paragraph testId="minority-owned-text">
                    <Icon size="small" name="info-circle" /> {translate(`${TRANSLATION_PATH}.shopAtMinorityOwnedPharmacy`)}
                  </Paragraph>
                )}
              </Stack>
              <Shelf.Spacer />
            </NearbyPharmacyDetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <NearbyPharmacyDetailSection alignment="top">
              <Bold>{translate(`${TRANSLATION_PATH}.address`)}: </Bold>
              <Paragraph testId="pharmacy-address">{pharmacyAddress}</Paragraph>
              <Shelf.Spacer />
            </NearbyPharmacyDetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <NearbyPharmacyDetailSection alignment="top">
              <Bold>{translate(`${TRANSLATION_PATH}.phone`)}: </Bold>
              <Paragraph testId="customer-support-number">{customerSupportNumber}</Paragraph>
              <Shelf.Spacer />
            </NearbyPharmacyDetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <NearbyPharmacyDetailSection alignment="top">
              <Bold>{translate(`${TRANSLATION_PATH}.hours`)}: </Bold>
              <Stack spacing="none" testId="hours-container">
                {(pharmacyHours || []).map((hour) => {
                  return <Paragraph key={hour}>{hour}</Paragraph>;
                })}
              </Stack>
              <Shelf.Spacer />
            </NearbyPharmacyDetailSection>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%" }} />}>
            <Stack spacing="none">
              <Shelf alignment="top">
                <Bold>{translate(`${TRANSLATION_PATH}.fluShots`)}: </Bold>
                <Paragraph testId="flu-shot-provider">{pharmacyServices?.immunizations_provided_indicator || "No"}</Paragraph>
                <Shelf.Spacer />
              </Shelf>
              <Shelf alignment="top">
                <Bold>{translate(`${TRANSLATION_PATH}.driveThru`)}: </Bold>
                <Paragraph testId="drive-thru-provider">{pharmacyServices?.drive_up_window_indicator || "No"}</Paragraph>
                <Shelf.Spacer />
              </Shelf>
              <Shelf alignment="top">
                <Bold>{translate(`${TRANSLATION_PATH}.languages`)}: </Bold>
                <Paragraph testId="languages">{(pharmacyLanguages || []).join(", ")}</Paragraph>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          </LoadingGroup.Placeholder>
        </Stack>
      </LoadingGroup>
    </div>
  );
};

export const FindBestPricePharmacyDetails: FC<PharmacyDetailsProps> = (props) => {
  const { t: translate } = useTranslation();

  const {
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    city,
    state,
    zip,
    customerSupportNumber,
    isLoading,
    name: pharmacyName,
    hours: pharmacyHours,
    services: pharmacyServices,
    languages: pharmacyLanguages,
    latitude,
    longitude,
    is_medicaid_enrolled: isMedicaidEnrolled,
  } = props;

  const { columns } = useResponsive({
    columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
  });

  return (
    <LoadingGroup loading={isLoading}>
      <Stack>
        <Heading>{translate(`${TRANSLATION_PATH}.pharmacyDetails`)}</Heading>
        <Panel appearance="primary" className="member-portal-panel">
          <Grid>
            <Grid.Span columns={12}>
              <FindBestPriceDetailSection
                title={translate(`${TRANSLATION_PATH}.billingProvider`)}
                content={pharmacyName}
              />
            </Grid.Span>
            <Grid.Span columns={columns}>
              <FindBestPriceDetailSection
                title={translate(`${TRANSLATION_PATH}.address`)}
                content={
                  <Link to={`https://maps.apple.com/?q=${encodeURIComponent(pharmacyName)}ll=${latitude},${longitude}`}>
                    {addressLine1 || ""} {addressLine2 || ""} {(!!addressLine1 || !!addressLine2) && <br />}
                    {city ? `${city},` : ""} {state || ""} {zip || ""}
                  </Link>
                }
              />
            </Grid.Span>
            <Grid.Span columns={columns}>
              <FindBestPriceDetailSection
                title={translate(`${TRANSLATION_PATH}.phone`)}
                content={
                  <Link to={`tel:${customerSupportNumber}`}>
                    {customerSupportNumber}
                  </Link>
                }
              />
            </Grid.Span>
            <Grid.Span columns={12}>
              <FindBestPriceDetailSection
                title={translate(`${TRANSLATION_PATH}.hours`)}
                content={(pharmacyHours || []).map((hour) => <Body key={hour}>{hour}</Body>)}
              />
            </Grid.Span>
            {!!pharmacyServices?.drive_up_window_indicator && (
              <Grid.Span columns={columns}>
                <FindBestPriceDetailSection
                  title={translate(`${TRANSLATION_PATH}.driveThru`)}
                  content={translate(`${TRANSLATION_PATH}.${pharmacyServices?.drive_up_window_indicator.toLowerCase()}`, { defaultValue: pharmacyServices?.drive_up_window_indicator })}
                  showLoading={false}
                />
              </Grid.Span>
            )}
            {pharmacyLanguages?.length > 0 && (
              <Grid.Span columns={columns}>
                <FindBestPriceDetailSection
                  title={translate(`${TRANSLATION_PATH}.languages`)}
                  content={pharmacyLanguages.join(", ")}
                  showLoading={false}
                />
              </Grid.Span>
            )}
            {!!pharmacyServices?.immunizations_provided_indicator && (
              <Grid.Span columns={columns}>
                <FindBestPriceDetailSection
                  title={translate(`${TRANSLATION_PATH}.immunizations`)}
                  content={translate(`${TRANSLATION_PATH}.${pharmacyServices?.immunizations_provided_indicator.toLowerCase()}`, { defaultValue: pharmacyServices?.immunizations_provided_indicator })}
                  showLoading={false}
                />
              </Grid.Span>
            )}
            {!!pharmacyServices?.immunizations_provided_indicator && (
              <Grid.Span columns={columns}>
                <FindBestPriceDetailSection
                  title={translate(`${TRANSLATION_PATH}.fluShots`)}
                  content={translate(`${TRANSLATION_PATH}.${pharmacyServices?.immunizations_provided_indicator.toLowerCase()}`, { defaultValue: pharmacyServices?.immunizations_provided_indicator })}
                  showLoading={false}
                />
              </Grid.Span>
            )}
            {!!isMedicaidEnrolled && (
              <Grid.Span columns={columns}>
                <FindBestPriceDetailSection
                  title={translate(`${TRANSLATION_PATH}.medicaidEnrolled`)}
                  content={translate(`${TRANSLATION_PATH}.${isMedicaidEnrolled.toLowerCase()}`, { defaultValue: isMedicaidEnrolled })}
                  showLoading={false}
                />
              </Grid.Span>
            )}
            <Grid.Span columns={12}>
              <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 288 }} />}>
                <AppleMap
                  style={{ height: 288, minHeight: "unset" }}
                  center={{ latitude: parseFloat(latitude), longitude: parseFloat(longitude) }}
                  locations={[{
                    latitude: parseFloat(latitude),
                    longitude: parseFloat(longitude),
                    title: pharmacyName,
                    data: {
                      ...props,
                    },
                  }]}
                />
              </LoadingGroup.Placeholder>
            </Grid.Span>
          </Grid>
        </Panel>
      </Stack>
    </LoadingGroup>
  );
};

export const PharmacyDetails: FC<PharmacyDetailsProps> = (props) => {
  const { context = "nearby_pharmacies", phone, useCustomerSupportHotline } = props;

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const customerSupportNumber: string = useMemo(() => {
    if (useCustomerSupportHotline) {
      return getCustomerSupportNumber(memberEligibility);
    } else if (phone) {
      return formatPhoneNumber(phone);
    }
    return "";
  }, [phone]);

  if (context === "find_best_price") {
    return <FindBestPricePharmacyDetails { ...props } customerSupportNumber={customerSupportNumber} />;
  }

  return <NearbyPharmacyDetails { ...props } customerSupportNumber={customerSupportNumber} />;
};
